import React, { Suspense, forwardRef, lazy } from 'react';

import { flexRender } from '@tanstack/react-table';
import cx from 'classnames';
import { isEmpty } from 'lodash';

import Loader from '@/widgets/Loader';
import { tableEmptyState } from '@/assets/images';

import Typography from '../Typography';
import { useDataTable } from './useDataTable';
import styles from './DataTable.module.scss';

const EMPTY_VIEW = {
	text: 'No data found',
	icon: tableEmptyState,
	subText: '',
};
const PAGINATION_LOADING = 'Loading pagination...';

const Pagination = lazy(() => import('@/widgets/Pagination'));

/**
 * @param {Object} props
 * @param {import('@tanstack/react-table').Column[]} props.columns
 */
function TableHeader({ columns }) {
	return (
		<thead>
			<tr className={cx(styles.header)}>
				{columns.map(({ columnDef, id }) => {
					/** @type { import('./useDataTable').ITableColumn } */
					const meta = columnDef.meta;
					return (
						<th
							{...(meta?.attributes ?? {})}
							className={cx(
								styles.cell,
								{
									[styles.truncate]: meta?.ellipsis,
								},
								meta?.className,
								styles[meta.align],
							)}
							key={id}
						>
							{flexRender(columnDef.header)}
						</th>
					);
				})}
			</tr>
		</thead>
	);
}

/**
 * @param {Object} props
 * @param {import('@tanstack/react-table').Row} props.row
 * @param {import('@tanstack/react-table').Row} props.rowHeight
 */
function TableCell({ row, rowHeight, columnClassName = '' }) {
	return row
		.getVisibleCells()
		.map(({ id, column: { columnDef }, getContext }) => (
			<td key={id}>
				<div
					className={cx(
						styles.cell,
						columnClassName,
						styles[columnDef.meta?.align],
					)}
					style={{
						height: rowHeight ?? 'auto',
					}}
				>
					<div className={cx(styles.data, columnDef.meta?.cellClassName)}>
						{flexRender(columnDef.cell, getContext())}
					</div>
				</div>
			</td>
		));
}

function TableBody({
	rows,
	onRowClick,
	rowUniqueKey,
	pageSize,
	rowClassName,
	columnClassName,
	rowHeight,
}) {
	return (
		<tbody>
			{rows.map((row, index, ogRows) => {
				// last row but with partial data
				const isPsuedoLastRow =
					ogRows.length - 1 === index && ogRows.length < pageSize;
				return (
					<tr
						onClick={() => onRowClick?.(row?.original, index)}
						className={cx(
							styles.row,
							onRowClick && styles.isInteractiveRow,
							isPsuedoLastRow && styles.psuedoLastRow,
							rowClassName,
						)}
						key={row?.original?.[rowUniqueKey] ?? row.id}
					>
						<TableCell
							columnClassName={columnClassName}
							row={row}
							rowHeight={rowHeight}
						/>
					</tr>
				);
			})}
		</tbody>
	);
}

function Table({
	table,
	onRowClick,
	pageSize,
	rowUniqueKey,
	rowClassName,
	columnClassName,
	rowHeight,
	ref,
}) {
	return (
		<table ref={ref} className={cx(styles.table)}>
			<TableHeader columns={table.getVisibleFlatColumns()} />
			<TableBody
				onRowClick={onRowClick}
				pageSize={pageSize}
				rowUniqueKey={rowUniqueKey}
				rows={table.getRowModel().rows}
				rowClassName={rowClassName}
				columnClassName={columnClassName}
				rowHeight={rowHeight}
			/>
		</table>
	);
}

function TablePagination({ pageNumber, pageSize, totalRows, changePage }) {
	return (
		<Suspense fallback={<span>{PAGINATION_LOADING}</span>}>
			<Pagination
				className={styles.paginator}
				currentPage={pageNumber}
				totalRows={totalRows}
				pageSize={pageSize}
				onPageChange={changePage}
			/>
		</Suspense>
	);
}

function TableLoader({ children }) {
	return (
		<div className={styles.loaderWrapper}>
			<div className={styles.defaultLoader}>{children}</div>
		</div>
	);
}

function TableEmptyState({ emptyView = {} }) {
	const { icon, text, subText } = { ...EMPTY_VIEW, ...emptyView };
	return (
		<div className={styles.tableEmptyContainer}>
			<img src={icon} alt="table empty view" />
			<Typography as="h4" weight="semibold">
				{text}
			</Typography>
			<Typography as="body" weight="font-normal">
				{subText}
			</Typography>
		</div>
	);
}

/**
 * @description DataTable component
 *
 * @author  Rohit Niraj Singh <@rohit.singh3>
 *
 * @reference https://tanstack.com/table/v8/docs/guide/introduction
 *
 * @returns {React.ReactElement}
 */

/**
 * @template D
 * @type {<D>(props: import('./useDataTable').ITableProps<D> & { ref?: import('react').ForwardedRef<HTMLTableElement> }) => React.ReactElement}
 */

const DataTable = forwardRef(
	(
		{
			data = [],
			columns = [],
			rowUniqueKey,
			hideColumns = [],
			pagination,
			onPageChange,
			onRowClick,
			rowHeight = 72,
			rowClassName,
			className,
			loading = false,
			loader = <Loader />,
			scrollable = true,
			emptyView = EMPTY_VIEW,
			columnClassName = '',
		},
		ref,
	) => {
		const {
			table,
			shouldPaginate,
			pageNumber,
			pageSize,
			totalRows,
			showScrollShadow,
			tableContainer,
			changePage,
		} = useDataTable({
			data,
			columns,
			hideColumns,
			pagination,
			onPageChange,
		});

		return (
			<div className={styles.mainContainer}>
				<div
					className={cx(
						styles.tableShadowContainer,
						scrollable && showScrollShadow && styles.withShadow,
					)}
				>
					<div
						ref={tableContainer}
						className={cx(
							styles.tableWrapper,
							className,
							loading && styles.loading,
							scrollable && styles.scrollableX,
						)}
					>
						{!isEmpty(data) ? (
							<Table
								onRowClick={onRowClick}
								pageSize={pageSize}
								rowClassName={rowClassName}
								ref={ref}
								rowHeight={rowHeight}
								rowUniqueKey={rowUniqueKey}
								table={table}
								columnClassName={columnClassName}
							/>
						) : null}
						{isEmpty(data) && !loading ? (
							<TableEmptyState emptyView={emptyView} />
						) : null}
						{loading ? <TableLoader> {loader} </TableLoader> : null}
					</div>
				</div>
				{shouldPaginate ? (
					<TablePagination
						changePage={changePage}
						pageNumber={pageNumber}
						pageSize={pageSize}
						totalRows={totalRows}
					/>
				) : null}
			</div>
		);
	},
);

DataTable.displayName = 'DataTable';

export default DataTable;
